import { CheckCircle, XCircle } from 'lucide-react';
import React from 'react';
import { CURRENCY_FIELDS, DATE_FIELDS, FIELD_LABELS } from '../../constants/fieldMapping.constants';
import { formatCurrency, formatDate, formatTime, isDateString } from '../../helpers/formatters.helpers';

interface FormattedFieldProps {
  fieldKey: string;
  value: any;
  className?: string;
}

export const FormattedField: React.FC<FormattedFieldProps> = ({ fieldKey, value, className = '' }) => {
  // Obtenir le label traduit
  const label = FIELD_LABELS[fieldKey] || formatFieldLabel(fieldKey);

  // Formater la valeur selon son type
  const formattedValue = formatValue(fieldKey, value);

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
      <div className="text-sm text-slate-800 font-medium">{formattedValue}</div>
    </div>
  );
};

/**
 * Formate une valeur selon son type et sa clé
 */
function formatValue(key: string, value: any): React.ReactNode {
  // Null/undefined
  if (value === null || value === undefined) {
    return <span className="text-slate-400 italic">Non renseigné</span>;
  }

  // Boolean
  if (typeof value === 'boolean') {
    return value ? (
      <span className="flex items-center gap-1.5 text-green-600">
        <CheckCircle className="w-4 h-4" />
        <span>Oui</span>
      </span>
    ) : (
      <span className="flex items-center gap-1.5 text-slate-400">
        <XCircle className="w-4 h-4" />
        <span>Non</span>
      </span>
    );
  }

  // Montant
  if (CURRENCY_FIELDS.includes(key) || (typeof value === 'number' && key.toLowerCase().includes('amount'))) {
    return <span className="font-bold text-slate-900">{formatCurrency(value)}</span>;
  }

  // Date
  if (DATE_FIELDS.includes(key) || isDateString(value)) {
    return formatDate(value);
  }

  // Heure
  if (key.toLowerCase().includes('time') && typeof value === 'string' && /^\d{2}:\d{2}/.test(value)) {
    return formatTime(value);
  }

  // String/Number par défaut
  return String(value);
}

/**
 * Convertit camelCase en Title Case
 */
function formatFieldLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
